/**
 * HOST STAMPS (2026-10-07): the few facts about its HOST WALL a door/window prototype
 * needs, copied onto the instance's own params (the prototype only ever sees the
 * opening): which side is the interior (sliding-door tracks), which local side faces
 * out (two-color frames), the host thickness (sliding gates) and the pocket body depth
 * (pocket doors). The wall generator stamps every instance it emits with this; the walk
 * mode builds its open/close parts from the SAME stamp, so an opened door is built with
 * the very geometry and colors it had closed (it used to rebuild from the bare element,
 * which put multi-slide tracks on the wrong side and dropped two-color frames).
 */
import type { ResolvedElement } from '@aj/bim-kernel'
import { isGateOp, isGarageOpenerOp, openingFrameFaces, resolveFrameMount, resolveOpeningFrame } from '@aj/bim-kernel'
import { slideInteriorSign } from './slideLanes'
import { findPocketRoom, pocketLayout } from './pocket'

export function stampOpeningForHost(
  opening: ResolvedElement, host: ResolvedElement | null | undefined, walls?: Iterable<ResolvedElement>,
): ResolvedElement {
  if (!host) return opening
  const extra: Record<string, number> = {}
  // EXTERIOR POCKETS: the gap to the room-side wall — found here when the caller has the
  // walls (walk mode); the wall generator's copy already carries it (hostedCuttingMap)
  if (walls && typeof opening.params.__pocketGap !== 'number') {
    const room = findPocketRoom(opening, host, walls)
    if (room) {
      extra.__pocketGap = room.gapFt
      opening = { ...opening, params: { ...opening.params, __pocketGap: room.gapFt } }
    }
  }
  if (opening.category === 'door' && opening.typeParams.operation === 'multiSlide') {
    const s = slideInteriorSign(String(opening.params.direction || 'L'), host.params.flipped === true)
    if (s !== 1) extra.__slideInterior = s
  }
  const t = host.assemblyThicknessFt
  if (opening.category === 'door' && isGateOp(String(opening.typeParams.operation)) && opening.params.__hostT !== t) {
    extra.__hostT = t
  }
  // GARAGE DOOR OPENER (2026-10-09): a sectional hangs on the wall face it parks toward —
  // that face as a local y off the instance (which sits at the frame's own centre, the
  // wall generator's mountOff — the same openingFrameFaces rule)
  if (opening.category === 'door' && isGarageOpenerOp(opening.typeParams.operation) && typeof t === 'number') {
    const merged = { ...opening.typeParams, ...opening.params }
    const mf = openingFrameFaces(
      resolveFrameMount(merged, host.typeParams?.function), host.params.flipped, host.assembly,
      resolveOpeningFrame('door', merged).depthFt, opening.params.frameRecessFt,
    )
    const fs = opening.params.facing === 'out' ? -1 : 1
    const face = Math.round((fs * t / 2 - (mf.lo + mf.hi) / 2) * 1e4) / 1e4
    if (opening.params.__garFace !== face) extra.__garFace = face
  }
  const oc = opening.openingColors
  if (oc && oc.exterior !== oc.interior) extra.__extSign = host.params.flipped ? -1 : 1
  // WINDOW SWING SIDE (2026-10-20): a window always learns which side is outside, so its
  // walk-mode sash opens to the side the plan swings it (the plan's wallFaceOffsets rule:
  // the exterior is the wall's +n face unless the wall is flipped). Only the walk motion
  // reads it for a one-colour window — its closed body and instancing key are unchanged.
  else if (opening.category === 'window') {
    const e = host.params.flipped ? -1 : 1
    if (opening.params.__extSign !== e) extra.__extSign = e
  }
  const pk = pocketLayout(opening, host)
  if (pk) extra.__pocketDepth = Math.round((pk.bodyHi - pk.bodyLo) * 1e4) / 1e4
  return Object.keys(extra).length ? { ...opening, params: { ...opening.params, ...extra } } : opening
}
